import { PlanningAssignment } from '../../../Models/resource.models';

/**
 * Reine Datums- und Spaltenlogik für die Interaktionen der Timeline
 * (Verschieben, Verlängern/Verkürzen, Auswahl). Bewusst ohne Angular- und
 * DOM-Abhängigkeiten, damit sie isoliert testbar ist.
 */

export type ResourceDragMode = 'move' | 'resize-start' | 'resize-end';

/** Beschreibung der sichtbaren Spalten einer Ansicht. */
export interface ResourceColumnLayout {
    /** Anzahl der Spalten (7 in der Woche, 28–31 im Monat, 13–14 im Quartal). */
    count: number;
    /** Erster Tag jeder Spalte (yyyy-MM-dd). */
    startDates: string[];
    /** Letzter Tag jeder Spalte (yyyy-MM-dd). */
    endDates: string[];
    /** Tage, um die ein Termin je Spaltenschritt verschoben wird (1 bzw. 7). */
    dayStep: number;
}

export function parseDateKey(value: string): { year: number; month: number; day: number } {
    const [year, month, day] = value.split('-').map(Number);
    return { year, month, day };
}

export function shiftDateKey(value: string, days: number): string {
    const { year, month, day } = parseDateKey(value);
    const date = new Date(Date.UTC(year, month - 1, day + days));

    return [
        date.getUTCFullYear(),
        String(date.getUTCMonth() + 1).padStart(2, '0'),
        String(date.getUTCDate()).padStart(2, '0')
    ].join('-');
}

export function daysBetweenKeys(start: string, end: string): number {
    const toUtc = (value: string): number => {
        const { year, month, day } = parseDateKey(value);
        return Date.UTC(year, month - 1, day);
    };

    return Math.round((toUtc(end) - toUtc(start)) / 86400000);
}

export function isWeekendKey(value: string): boolean {
    const { year, month, day } = parseDateKey(value);
    const weekday = new Date(Date.UTC(year, month - 1, day)).getUTCDay();
    return weekday === 0 || weekday === 6;
}

/**
 * Spaltenindex zu einer X-Position innerhalb eines Rasters. Die Position wird
 * auf das Raster begrenzt, damit ein Loslassen knapp außerhalb noch auf die
 * erste bzw. letzte Spalte fällt.
 */
export function columnIndexFromX(
    clientX: number,
    rectLeft: number,
    rectWidth: number,
    columnCount: number
): number {
    if (columnCount <= 0 || rectWidth <= 0) {
        return 0;
    }

    const relativeX = Math.max(0, Math.min(rectWidth - 0.001, clientX - rectLeft));
    const index = Math.floor(relativeX / (rectWidth / columnCount));

    return Math.max(0, Math.min(columnCount - 1, index));
}

/** Spaltenindex, in dem ein Datum liegt (-1 = außerhalb des Zeitraums). */
export function columnIndexOfDate(layout: ResourceColumnLayout, date: string): number {
    return layout.startDates.findIndex(
        (start, index) => date >= start && date <= layout.endDates[index]
    );
}

export interface ResourceInteractionChange {
    mode: ResourceDragMode;
    /** Spalte, in der der Balken angefasst wurde. */
    grabColumn: number;
    /** Spalte, über der losgelassen (oder aktuell gezogen) wird. */
    targetColumn: number;
    /** Zeile, in der angefasst wurde. */
    sourceEmployeeId: string;
    /** Zeile, über der losgelassen wird. */
    targetEmployeeId: string;
}

/**
 * Wendet eine Interaktion auf einen Termin an und liefert eine tiefe Kopie mit
 * den neuen Werten. Gibt null zurück, wenn sich nichts ändert.
 *
 * - Verschieben: Verschiebung = (Zielspalte - Griffspalte) x dayStep Tage. Die
 *   Dauer bleibt erhalten. Bei Terminen mit genau einem Teilnehmer wechselt
 *   der Mitarbeiter, wenn in einer anderen Zeile losgelassen wird; bei
 *   mehreren Teilnehmern bleiben alle erhalten.
 * - Verlängern/Verkürzen: in Tagesansichten tageweise, in der Quartalsansicht
 *   auf Wochenanfang (links) bzw. Wochenende (rechts). Beginn und Ende werden
 *   so begrenzt, dass das Ende nie vor dem Beginn liegt.
 */
export function applyInteraction(
    assignment: PlanningAssignment,
    layout: ResourceColumnLayout,
    change: ResourceInteractionChange
): PlanningAssignment | null {

    const updated: PlanningAssignment = {
        ...assignment,
        participants: assignment.participants.map(p => ({ ...p })),
        weekendDates: [...assignment.weekendDates],
        weekendDays: (assignment.weekendDays ?? []).map(day => ({
            date: day.date,
            hoursByEmployee: { ...day.hoursByEmployee }
        }))
    };

    if (change.mode === 'move') {
        const offset = (change.targetColumn - change.grabColumn) * layout.dayStep;
        const employeeChanges =
            updated.participants.length === 1 &&
            change.sourceEmployeeId !== change.targetEmployeeId;

        if (offset === 0 && !employeeChanges) {
            return null;
        }

        updated.startDate = shiftDateKey(assignment.startDate, offset);
        updated.endDate = shiftDateKey(assignment.endDate, offset);

        updated.weekendDays = (assignment.weekendDays ?? [])
            .map(day => ({
                date: shiftDateKey(day.date, offset),
                hoursByEmployee: { ...day.hoursByEmployee }
            }))
            .filter(day => isWeekendKey(day.date));

        updated.weekendDates = assignment.weekendDates
            .map(date => shiftDateKey(date, offset))
            .filter(date => isWeekendKey(date));

        if (employeeChanges) {
            const source = change.sourceEmployeeId;
            const target = change.targetEmployeeId;

            updated.participants[0].employeeId = target;

            updated.weekendDays = updated.weekendDays.map(day => ({
                date: day.date,
                hoursByEmployee: { [target]: day.hoursByEmployee[source] ?? 0 }
            }));
        }

        return updated;
    }

    const target = Math.max(0, Math.min(layout.count - 1, change.targetColumn));

    if (change.mode === 'resize-start') {
        let newStart = layout.startDates[target];
        if (newStart > updated.endDate) {
            newStart = updated.endDate;
        }
        if (newStart === assignment.startDate) {
            return null;
        }
        updated.startDate = newStart;
    }

    if (change.mode === 'resize-end') {
        let newEnd = layout.endDates[target];
        if (newEnd < updated.startDate) {
            newEnd = updated.startDate;
        }
        if (newEnd === assignment.endDate) {
            return null;
        }
        updated.endDate = newEnd;
    }

    // Wochenendplanung auf den neuen Zeitraum begrenzen.
    updated.weekendDates = updated.weekendDates.filter(
        date => date >= updated.startDate && date <= updated.endDate
    );
    updated.weekendDays = (updated.weekendDays ?? []).filter(
        day => updated.weekendDates.includes(day.date)
    );

    return updated;
}
